import React from "react";
import { useNavigate } from "react-router-dom";



const conditionCards = [
  {
    title: 'Family History Risk',
    desc: 'A parent, sibling, or close relative with early heart disease can indicate inherited lipid disorders, metabolic vulnerability, or familial hypertension.',
    iconBg: '#FADBD8',
    iconColor: '#C0392B',
    iconPath: require('../assets/symptoms/familyHis.png'),
  },
  {
    title: 'Genetic Risk Screening',
    desc: 'Combines personal history, family history, and clinical findings for a comprehensive view of future cardiovascular risk — before symptoms appear.',
    iconBg: '#EAF3DE',
    iconColor: '#3B6D11',
    iconPath: require('../assets/symptoms/geneticRiskIcon.png'),
  },
  {
    title: 'Heart Disease Risk Factors',
    desc: 'High blood pressure, cholesterol, diabetes, smoking, obesity, inactivity, and age are the most common risk factors — many of which are modifiable.',
    iconBg: '#FAEEDA',
    iconColor: '#854F0B',
    iconPath: require('../assets/symptoms/riskFactor.png'),
  },
  {
    title: 'Risk Scoring & Estimation',
    desc: 'Tools like ASCVD calculators and the AHA PREVENT framework translate clinical data into a clearer projection of future heart risk.',
    iconBg: '#E6F1FB',
    iconColor: '#185FA5',
    iconPath: require('../assets/symptoms/riskScoring.png'),
  },
];

const riskAssessmentItems = [
  'Age, blood pressure, cholesterol, and blood sugar levels',
  'Smoking status, body weight, and exercise habits',
  'Diet quality, stress exposure, and sleep patterns',
  'Past medical history and current medications',
  'Chronic kidney disease, inflammatory disease, or metabolic syndrome',
  'Family history of premature atherosclerotic cardiovascular disease',
];

const benefitGroups = [
  'Adults with a family history of early heart disease',
  'Individuals with borderline cholesterol or blood pressure readings',
  'Patients with diabetes or prediabetes',
  'Smokers or former smokers',
  'Individuals with obesity or a sedentary lifestyle',
  'Anyone who prefers to be proactive about long-term heart health',
];

const consultationExpectations = [
  'Review of symptoms, family history, and medication history',
  'Assessment of previous tests and personal health concerns',
  'Guidance on blood, cholesterol, and blood pressure monitoring',
  'Glucose testing, ECG, or echocardiography where clinically indicated',
  'A personalized, evidence-based prevention strategy',
  'Long-term follow-up planning to minimize uncertainty and risk',
];

const qaItems = [
  {
    q: 'Does a genetic risk mean heart disease is inevitable?',
    a: 'Not at all. Inherited risk means earlier awareness, closer surveillance, and smarter prevention — not a predetermined outcome. Genetics combined with lifestyle guidance makes prevention far more precise.',
  },
  {
    q: 'What is familial hypercholesterolemia?',
    a: 'An inherited condition that elevates cholesterol levels from a young age, significantly increasing future cardiac risk if left unmanaged. Early detection is key to effective treatment.',
  },
  {
    q: 'Who should consider a preventive risk assessment?',
    a: 'Anyone with a concerning family history, borderline risk markers, or a personal desire to understand and manage their long-term cardiovascular risk — not just high-risk patients.',
  },
];

/* ─── Sub-components ────────────────────────────────────────────────── */

function HeroBanner() {
  return (
    <div className="ihb-hero">
      <img
        src={require('../assets/symptoms/geneticRisk.png')}
        alt="Chest pain and cardiovascular care"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <div className="ihb-hero-overlay" />
      <div className="ihb-hero-content">
        <span className="ihb-hero-tag">Preventive Cardiology</span>
        <h1>Preventive &amp; Genetic Risk Assessment</h1>
        <p>
          Heart problems need not start with symptoms. Identify your risk early —
          before a crisis — and build a prevention plan that is personal, informed,
          and effective.
        </p>
      </div>
    </div>
  );
}

function Byline() {
  return (
    <div className="ihb-byline">
      <div className="ihb-byline-dot">CV</div>
      <div className="ihb-byline-text">
        <strong>Cardiovascular Physician — Dr. Siam Al Mobarak</strong>
        <span>
          Preventive genetic risk screening, heart disease risk assessment &amp;
          cardiovascular prevention
        </span>
      </div>
    </div>
  );
}

function ConditionCard({ title, desc, iconBg, iconColor, iconPath }) {
  return (
    <div className="ihb-rhythm-card">
      <div className="ihb-icon" >
        <img src={iconPath} alt="" />
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function QAItem({ q, a }) {
  return (
    <div className="ihb-qa-item">
      <div className="ihb-qa-q">{q}</div>
      <div className="ihb-qa-a">{a}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="ihb-treatment-list">
      {items.map((item) => (
        <li key={item}>
          <span className="ihb-bullet" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ─── Main Page Component ───────────────────────────────────────────── */

export default function PreventiveGeneticRiskAssessment() {
  const navigate = useNavigate();
  return (
    <div className="ihb-body">

      {/* ── Hero ── */}
      <HeroBanner />

      <div className="ihb-page">

        {/* ── Byline ── */}
        <Byline />

        {/* ── Lead ── */}
        <p className="ihb-lead">
          Risk accumulates silently over time — through inherited features, daily
          routines, and unattended illnesses. Preventive evaluation reveals warning
          signs early, particularly for those with a family history of heart disease,
          high blood pressure, diabetes, or other established risk factors.
        </p>

        {/* ── Why It Matters ── */}
        <section className="ihb-section">
          <h2>Why Does This Assessment Matter?</h2>
          <p>
            Many patients seek care before a significant event occurs. They want to
            understand their long-term risk and take action now to minimize it. This
            is where Preventive Genetic Risk Screening is most valuable. It combines
            personal history, family history, clinical findings, and targeted risk
            review to form a far more comprehensive view of future cardiovascular
            risk.
          </p>
          <p>
            Rather than waiting for chest pain, breathlessness, or a heart attack,
            patients can act earlier — with informed medical guidance and a specific
            action plan for prevention. Evidence-based prevention pathways from the
            CDC, NHLBI, ACC, and AHA consistently emphasize risk awareness and early
            action as the cornerstone of effective cardiovascular care.
          </p>
        </section>

        {/* ── Condition Cards ── */}
        <section className="ihb-section">
          <h2>What We Screen &amp; Evaluate</h2>
          <div className="ihb-card-grid">
            {conditionCards.map((card) => (
              <ConditionCard key={card.title} {...card} />
            ))}
          </div>
        </section>

        {/* ── Family History ── */}
        <section className="ihb-section">
          <h2>Family History of Heart Disease</h2>
          <p>
            Family history is one of the most significant components of a preventive
            consultation. If a parent, sibling, or close relative developed heart
            disease early in life, your own risk may be considerably greater. This
            can indicate genetic lipid disorders, shared metabolic vulnerability, or
            a family pattern of hypertension, diabetes, smoking, or sedentary
            lifestyle.
          </p>
          <p>
            In some families, early cardiac illness may indicate inherited disorders
            such as familial hypercholesterolemia — a condition that can elevate
            cholesterol at a young age and greatly increase the risk of future
            cardiac disease if left unmanaged. A thorough family history review
            helps determine the appropriate intensity of screening and the most
            meaningful next steps.
          </p>
        </section>

        {/* ── Callout ── */}
        <div className="ihb-callout" role="note">
          <p>
            <strong>Key insight:</strong> Inherited risk does not mean inevitable
            disease. It means earlier awareness, closer surveillance, and smarter
            prevention. When genetics is considered alongside lifestyle, prevention
            becomes more specific, personal, and far more effective.
          </p>
        </div>

        {/* ── What Is Reviewed ── */}
        <section className="ihb-section">
          <h2>What Is Reviewed During Risk Assessment</h2>
          <p>
            An effective consultation goes beyond asking whether you feel well. A
            comprehensive Heart Disease Risk Assessment examines multiple factors —
            because heart risk is typically the combination of several issues, not a
            single independent problem. The review covers:
          </p>
          <BulletList items={riskAssessmentItems} />
        </section>

        {/* ── Risk Scoring ── */}
        <section className="ihb-section">
          <h2>Risk Scoring &amp; Individual Estimation</h2>
          <p>
            Risk scoring translates clinical information into a clearer projection of
            future heart risk. Standard tools such as ASCVD-based calculators and the
            newer AHA PREVENT framework guide preventive care — particularly in adults
            with no known heart disease. These instruments do not replace clinical
            decision-making, but they help organize thinking around follow-up timing,
            lifestyle goals, and whether medication may be beneficial.
          </p>
          <p>
            Risk scoring is most useful when considered alongside family history,
            physical examination, and laboratory results. A single generated score
            may not be the only means of identifying inherited danger or long-term
            cumulative exposure.
          </p>
        </section>

        {/* ── Common Risk Factors ── */}
        <section className="ihb-section">
          <h2>Understanding Common Risk Factors</h2>
          <p>
            High blood pressure, high cholesterol, diabetes, smoking, obesity,
            physical inactivity, unhealthy eating patterns, family history, and age
            are the most prevalent Heart Disease Risk Factors. Not all risks are
            modifiable — but many are. This is precisely why prevention is effective.
            Once patients understand which risks are inherent and which are amenable
            to change, they are better positioned to make meaningful improvements
            over time.
          </p>
          <p>
            Even modest advances in blood pressure management, tobacco abstinence,
            physical activity, weight control, and cholesterol reduction translate
            into significantly better long-term outcomes. Small changes, sustained
            consistently, matter enormously.
          </p>
        </section>

        {/* ── Lifestyle & Preventive Cardiology ── */}
        <section className="ihb-section">
          <h2>Lifestyle &amp; Preventive Cardiology</h2>
          <p>
            Preventive cardiology is not only about testing — it is about action.
            With risk clearly identified, a clinician can assist in building a
            realistic plan covering food choices, physical activity goals, weight
            management, sleep quality, hypertension control, lipid management,
            glucose monitoring, and smoking cessation.
          </p>
          <p>
            This is where Genetic Prevention for Cardiovascular Diseases is
            particularly valuable. Knowing your inherited risk profile allows
            prevention to be targeted precisely where it matters most — earlier,
            more personally, and more efficiently than a one-size-fits-all approach.
          </p>
        </section>

        {/* ── Who May Benefit ── */}
        <section className="ihb-section">
          <h2>Who May Benefit Most</h2>
          <p>
            This consultation is particularly valuable for the following individuals
            — though it is not limited to those already considered high risk.
            Prevention is for anyone who wants to stay ahead of future Cardiovascular
            Diseases through informed, personalized care:
          </p>
          <BulletList items={benefitGroups} />
        </section>

        {/* ── Q&A ── */}
        <section className="ihb-section">
          <h2>Common Questions</h2>
          <div className="ihb-qa-section">
            {qaItems.map((item) => (
              <QAItem key={item.q} {...item} />
            ))}
          </div>
        </section>

        {/* ── What to Expect ── */}
        <section className="ihb-section">
          <h2>What to Expect During an Online Consultation</h2>
          <p>
            An online preventive consultation is structured, focused, and practical.
            The aim is not over-testing — it is to pinpoint actual risk, reduce
            uncertainty, and establish an appropriate level of follow-up. Based on
            the initial review, each patient receives guidance tailored to their
            specific picture:
          </p>
          <BulletList items={consultationExpectations} />
          <p style={{ marginTop: '1rem' }}>
            This is a realistic method for patients who want clarity, continuity,
            and long-term planning — rather than waiting for disease to become
            advanced before seeking help.
          </p>
        </section>

        {/* ── Urgent Box ── */}
        <div className="ihb-urgent-box" role="note">
          <h2>A Proactive Step Toward Lifelong Heart Health</h2>
          <p>
            The most effective preventive care is delivered early and personally.
            Targeted genetic and risk-based consultation can reveal hidden patterns,
            clarify their meaning, and transform concern into a clear prevention
            roadmap. For patients with multiple risk factors or a concerning family
            history, this service offers a smart, confident first step — one that
            promotes earlier detection, more personalized care, and stronger
            long-term heart health. Prevention is not about fear. It is about
            transparency, control, and making informed decisions before problems
            become emergencies.
          </p>
        </div>

        {/* ── Footer CTA ── */}
        <div className="ihb-footer-cta">
          <p>
            Concerned about your family history or long-term heart risk? Start your
            preventive genetic risk assessment today and take control of your
            cardiovascular future — before symptoms arise.
          </p>
          <button className="ihb-cta-btn" onClick={() => navigate('/consultations')}>Begin Your Risk Assessment</button>
        </div>

      </div>
    </div>
  );
}